import { useState } from "react";
import {
  api, ApiError,
  type LandingCharacteristicScore, type LandingPage, type LandingPageFilter, type ProductRow,
} from "../api";
import { useAsync } from "../useAsync";
import { count, delta, shortTime, toneColor, DASH } from "../format";

const RANGES = [7, 30, 90];

const SOURCE_LABELS: Record<LandingPage["source"], string> = {
  manual: "manual",
  ga4: "GA4",
  clarity: "Clarity",
  posthog: "PostHog",
};

/** Match a path against a prefix or glob pattern. */
export function matchesPath(pattern: string, path: string): boolean {
  if (!pattern || !path) return false;
  let p = pattern.trim().toLowerCase();
  const target = path.trim().toLowerCase();

  if (!p.startsWith("/") && !p.startsWith("*")) {
    p = "/" + p;
  }

  if (p.includes("*") || p.includes("?")) {
    const escaped = p
      .replace(/[.+^${}()|[\]\\]/g, "\\$&")
      .replace(/\*/g, ".*")
      .replace(/\?/g, ".");
    const re = new RegExp(`^${escaped}$`, "i");
    return re.test(target);
  }

  if (p.endsWith("/")) {
    const prefixNoSlash = p.slice(0, -1);
    return target === prefixNoSlash || target.startsWith(p);
  }

  return target === p || target.startsWith(p + "/") || target.startsWith(p + "?");
}

/** Suggest a sensible exclude pattern for a given path (e.g. /deals/abc -> /deals). */
function suggestPattern(path: string): string {
  const parts = path.split("/").filter(Boolean);
  if (parts.length > 1) {
    return `/${parts[0]}`;
  }
  return path;
}

/**
 * Pages: conversion characteristics from the warehouse plus an on-demand
 * weighted quality score. The score is headline + one metric + per-char
 * rationales; longer reasoning stays behind a disclosure.
 */
export function LandingPages({ product }: { product: ProductRow }) {
  const [days, setDays] = useState(30);
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [openId, setOpenId] = useState<string | null>(null);

  // Exclude filters state
  const [excludeActive, setExcludeActive] = useState(true);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [newPattern, setNewPattern] = useState("");
  const [addingFilter, setAddingFilter] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [filterBusy, setFilterBusy] = useState<Record<string, boolean>>({});

  const list = useAsync(() => api.landingPages.list(product.slug, days), [product.slug, days]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = url.trim();
    if (!next) return;
    setAdding(true);
    setAddError(null);
    try {
      await api.landingPages.add(product.slug, next);
      setUrl("");
      list.reload();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAdding(false);
    }
  };

  const analyze = async (id: string) => {
    setBusyId(id);
    setRowError((e) => { const { [id]: _d, ...rest } = e; return rest; });
    try {
      await api.landingPages.analyze(product.slug, id);
      list.reload();
      setOpenId(id);
    } catch (err) {
      setRowError((e) => ({ ...e, [id]: err instanceof ApiError ? err.message : String(err) }));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    setBusyId(id);
    try {
      await api.landingPages.remove(product.slug, id);
      if (openId === id) setOpenId(null);
      list.reload();
    } catch (err) {
      setRowError((e) => ({ ...e, [id]: err instanceof ApiError ? err.message : String(err) }));
    } finally {
      setBusyId(null);
    }
  };

  const addFilter = async (pattern: string) => {
    const p = pattern.trim();
    if (!p) return;
    setAddingFilter(true);
    setFilterError(null);
    try {
      await api.landingPages.filters.add(product.slug, p, true);
      setNewPattern("");
      list.reload();
    } catch (err) {
      setFilterError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAddingFilter(false);
    }
  };

  const toggleFilter = async (id: string, enabled: boolean) => {
    setFilterBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.landingPages.filters.update(product.slug, id, { enabled });
      list.reload();
    } catch (err) {
      setFilterError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setFilterBusy((b) => { const { [id]: _d, ...rest } = b; return rest; });
    }
  };

  const removeFilter = async (id: string) => {
    setFilterBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.landingPages.filters.remove(product.slug, id);
      list.reload();
    } catch (err) {
      setFilterError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setFilterBusy((b) => { const { [id]: _d, ...rest } = b; return rest; });
    }
  };

  const onQuickExclude = (pagePath: string) => {
    const suggested = suggestPattern(pagePath);
    setNewPattern(suggested);
    setFilterPanelOpen(true);
  };

  if (list.error) return <PageProblem message={list.error} />;
  if (list.loading || !list.data) return <PageLoading />;
  const pages = list.data.pages;
  const filters: LandingPageFilter[] = list.data.filters ?? [];
  const activeFilters = filters.filter((f) => f.enabled);

  // Determine which filter matched each page
  const pageMatchMap = new Map<string, string | null>();
  for (const p of pages) {
    const matched = activeFilters.find((f) => matchesPath(f.pattern, p.path));
    pageMatchMap.set(p.id, matched ? matched.pattern : null);
  }

  const visiblePages = excludeActive
    ? pages.filter((p) => !pageMatchMap.get(p.id))
    : pages;

  const excludedCount = pages.length - pages.filter((p) => !pageMatchMap.get(p.id)).length;

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Pages</h1>
          <span className="sub">
            Landing pages for {product.name}: conversion characteristics from traffic,
            and a weighted quality score on demand.
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((d) => (
            <button key={d} type="button" className="seg" aria-pressed={days === d} onClick={() => setDays(d)}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={add} style={{ display: "flex", gap: 10, marginTop: 22, flexWrap: "wrap", alignItems: "center" }}>
        <input
          className="input"
          type="url"
          required
          placeholder="https://example.com/pricing"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={{ flex: 1, minWidth: 240 }}
          aria-label="Landing page URL"
        />
        <button type="submit" className="btn-solid" disabled={adding}>
          {adding ? "Adding…" : "Add page"}
        </button>
      </form>
      {addError && <div className="form-error" style={{ marginTop: 8 }}>{addError}</div>}

      {/* Filter Control Bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setFilterPanelOpen((v) => !v)}
          aria-expanded={filterPanelOpen}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            minHeight: 36,
            padding: "0 12px",
            fontSize: 13,
            background: filterPanelOpen ? "var(--solid)" : undefined,
          }}
        >
          <span>Exclude filters</span>
          {filters.length > 0 && (
            <span
              className="eyebrow"
              style={{
                background: "var(--surface-inset)",
                padding: "2px 6px",
                borderRadius: 4,
                color: "var(--text-2)",
              }}
            >
              {filters.length}
            </span>
          )}
        </button>

        {filters.length > 0 && (
          <button
            type="button"
            className="seg"
            aria-pressed={excludeActive}
            onClick={() => setExcludeActive((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              minHeight: 36,
              padding: "0 12px",
              fontSize: 12.5,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: excludeActive ? "var(--pos)" : "var(--muted)",
              }}
            />
            <span>
              {excludeActive
                ? `Excluding ${excludedCount} page${excludedCount === 1 ? "" : "s"}`
                : "Exclude filters paused (showing all)"}
            </span>
          </button>
        )}

        {filters.length === 0 && !filterPanelOpen && (
          <button
            type="button"
            className="btn-text"
            onClick={() => setFilterPanelOpen(true)}
            style={{ fontSize: 12.5, color: "var(--text-3)" }}
          >
            + Add exclude filter (e.g. /deals)
          </button>
        )}

        {excludeActive && excludedCount > 0 && (
          <span style={{ fontSize: 12.5, color: "var(--text-faint)", marginLeft: "auto" }}>
            Showing {visiblePages.length} of {pages.length} pages
          </span>
        )}
      </div>

      {/* Filter Management Drawer / Panel */}
      {filterPanelOpen && (
        <div
          style={{
            marginTop: 12,
            padding: "16px 18px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 550, color: "var(--text)" }}>
                Saved Exclude Filters
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-4)", marginTop: 2, lineHeight: 1.45 }}>
                Pages matching these patterns are hidden from the table and excluded from analysis.
                Patterns like <code style={{ fontFamily: "var(--font-mono)" }}>/deals</code> match{" "}
                <code style={{ fontFamily: "var(--font-mono)" }}>/deals</code> and all subpaths like{" "}
                <code style={{ fontFamily: "var(--font-mono)" }}>/deals/123</code>.
                Wildcards like <code style={{ fontFamily: "var(--font-mono)" }}>/guides/*</code> are also supported.
              </div>
            </div>
            <button
              type="button"
              className="btn-text"
              onClick={() => setFilterPanelOpen(false)}
              style={{ fontSize: 12, minHeight: 0, padding: 0 }}
            >
              Close
            </button>
          </div>

          {/* Add Filter Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void addFilter(newPattern);
            }}
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
          >
            <input
              className="input"
              type="text"
              placeholder="e.g. /deals or /guides/*"
              value={newPattern}
              onChange={(e) => setNewPattern(e.target.value)}
              style={{ flex: 1, minWidth: 200, minHeight: 36, fontSize: 13 }}
              aria-label="Exclude pattern"
            />
            <button
              type="submit"
              className="btn-solid"
              disabled={addingFilter || !newPattern.trim()}
              style={{ minHeight: 36, padding: "0 14px", fontSize: 13 }}
            >
              {addingFilter ? "Saving…" : "Add exclude filter"}
            </button>
          </form>
          {filterError && <div className="form-error">{filterError}</div>}

          {/* Filters List */}
          {filters.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "var(--text-faint)", fontStyle: "italic" }}>
              No exclude filters defined yet. Add a path pattern above to exclude specific sections (e.g. /deals).
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filters.map((f) => {
                const matchCount = pages.filter((p) => matchesPath(f.pattern, p.path)).length;
                const isBusy = Boolean(filterBusy[f.id]);
                return (
                  <div
                    key={f.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px 12px",
                      background: "var(--surface-sunk)",
                      border: "1px solid var(--border-hairline)",
                      borderRadius: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      className="num"
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: f.enabled ? "var(--text)" : "var(--text-faint)",
                        textDecoration: f.enabled ? "none" : "line-through",
                      }}
                    >
                      {f.pattern}
                    </span>

                    <span
                      className="eyebrow"
                      style={{
                        background: "var(--surface-inset)",
                        padding: "2px 7px",
                        borderRadius: 4,
                        color: "var(--text-3)",
                      }}
                    >
                      {matchCount} page{matchCount === 1 ? "" : "s"} match
                    </span>

                    <span style={{ flex: 1 }} />

                    <button
                      type="button"
                      className="seg"
                      aria-pressed={f.enabled}
                      onClick={() => void toggleFilter(f.id, !f.enabled)}
                      disabled={isBusy}
                      style={{ minHeight: 28, padding: "2px 10px", fontSize: 11.5 }}
                    >
                      {f.enabled ? "Active" : "Disabled"}
                    </button>

                    <button
                      type="button"
                      className="btn-text"
                      onClick={() => void removeFilter(f.id)}
                      disabled={isBusy}
                      style={{ color: "var(--neg)", fontSize: 12, minHeight: 28, padding: "0 4px" }}
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Pages List */}
      {pages.length === 0 ? (
        <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 12, padding: "28px 24px",
                      marginTop: 28, fontSize: 13.5, lineHeight: 1.6, color: "var(--text-3)", background: "var(--surface)" }}>
          No landing pages yet. Add a URL above, or connect GA4, PostHog or Clarity — the
          next sync will pick up the top paths by sessions and recordings.
        </div>
      ) : visiblePages.length === 0 ? (
        <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 12, padding: "28px 24px",
                      marginTop: 24, fontSize: 13.5, lineHeight: 1.6, color: "var(--text-3)", background: "var(--surface)" }}>
          All {pages.length} landing pages match your active exclude filters.{" "}
          <button
            type="button"
            className="btn-link"
            onClick={() => setExcludeActive(false)}
            style={{ fontSize: 13.5 }}
          >
            Turn off exclude filters
          </button>{" "}
          to view them, or{" "}
          <button
            type="button"
            className="btn-link"
            onClick={() => setFilterPanelOpen(true)}
            style={{ fontSize: 13.5 }}
          >
            edit your filters
          </button>.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 10 }}>
          {visiblePages.map((p) => {
            const matchedFilterPattern = pageMatchMap.get(p.id);
            return (
              <PageRow
                key={p.id}
                page={p}
                open={openId === p.id}
                busy={busyId === p.id}
                error={rowError[p.id]}
                matchedFilter={!excludeActive && matchedFilterPattern ? matchedFilterPattern : undefined}
                onToggle={() => setOpenId(openId === p.id ? null : p.id)}
                onAnalyze={() => analyze(p.id)}
                onRemove={() => remove(p.id)}
                onQuickExclude={() => onQuickExclude(p.path)}
              />
            );
          })}
        </div>
      )}

      <div className="banner" style={{ marginTop: 26 }}>
        <span>
          Quality scores are on-demand (one model call per Analyse) against a
          fixed weighted rubric. Conversion numbers come from GA4 landing-page
          traffic — they are not invented by the model.
        </span>
      </div>
    </div>
  );
}

function PageRow({
  page, open, busy, error, matchedFilter, onToggle, onAnalyze, onRemove, onQuickExclude,
}: {
  page: LandingPage;
  open: boolean;
  busy: boolean;
  error?: string;
  matchedFilter?: string;
  onToggle: () => void;
  onAnalyze: () => void;
  onRemove: () => void;
  onQuickExclude: () => void;
}) {
  const t = page.traffic;
  const a = page.latest_analysis;
  const sessD = delta(t.sessions, t.sessions_prev, "up");
  const convD = delta(t.conversion_rate, t.conversion_rate_prev, "up");

  return (
    <div
      style={{
        borderBottom: "1px solid var(--border-hairline)",
        padding: "16px 2px 14px",
        opacity: matchedFilter ? 0.7 : 1,
      }}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" onClick={onToggle} className="btn-text"
                style={{ minHeight: 0, padding: 0, textAlign: "left", flex: "1 1 200px" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14.5, fontWeight: 550 }}>{page.path}</span>
            <span className="eyebrow">{SOURCE_LABELS[page.source]}</span>
            {matchedFilter && (
              <span
                className="eyebrow"
                style={{
                  background: "var(--surface-inset)",
                  color: "var(--text-3)",
                  padding: "1px 6px",
                  borderRadius: 4,
                }}
              >
                excluded by {matchedFilter}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 3, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420 }}>
            {page.url}
          </div>
        </button>

        <Stat label="Sessions" value={count(t.sessions)} deltaLabel={sessD?.label} tone={sessD?.tone} />
        <Stat label="Engaged" value={pct(t.engagement_rate)} />
        <Stat label="Conv." value={pct(t.conversion_rate)} deltaLabel={convD?.label} tone={convD?.tone} />
        {page.friction && (page.friction.rage_clicks > 0 || page.friction.dead_clicks > 0) && (
          <Stat
            label="Friction"
            value={`${page.friction.rage_clicks + page.friction.dead_clicks}`}
            deltaLabel={page.friction.rage_clicks > 0 ? `${page.friction.rage_clicks} rage` : `${page.friction.dead_clicks} dead`}
            tone="neg"
          />
        )}
        <MiniDial score={a?.status === "ok" ? a.overall_score : null} />

        <div style={{ display: "flex", gap: 8, marginLeft: "auto", alignItems: "center" }}>
          {page.friction?.recordings_url && (
            <a className="btn-ghost" href={page.friction.recordings_url} target="_blank" rel="noreferrer"
               title={`Open recordings in ${page.friction.provider === "clarity" ? "Microsoft Clarity" : "PostHog"}`}
               style={{ minHeight: 36, padding: "0 12px", fontSize: 13, display: "inline-flex", alignItems: "center" }}>
              Watch
            </a>
          )}
          <button type="button" className="btn-solid" onClick={onAnalyze} disabled={busy}
                  style={{ minHeight: 36, padding: "0 12px", fontSize: 13 }}>
            {busy ? "Analysing…" : a ? "Re-analyse" : "Analyse"}
          </button>
          <button
            type="button"
            className="btn-text"
            onClick={onQuickExclude}
            disabled={busy}
            title="Add an exclude filter for this path or section"
            style={{ fontSize: 12.5 }}
          >
            Exclude
          </button>
          <button type="button" className="btn-text" onClick={onRemove} disabled={busy}
                  style={{ color: "var(--neg)" }}>
            Remove
          </button>
        </div>
      </div>

      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}

      {open && (
        <div style={{ marginTop: 14, padding: "14px 16px 12px", background: "var(--surface)",
                      border: "1px solid var(--border)", borderRadius: 12 }}>
          {!a ? (
            <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.55 }}>
              Not analysed yet. Analyse fetches the live page and scores it against the rubric.
            </div>
          ) : a.status === "error" ? (
            <div style={{ fontSize: 13, color: "var(--neg)", lineHeight: 1.55 }}>
              Last run failed{a.analyzed_at ? ` · ${shortTime(a.analyzed_at)}` : ""}.
              Try again, or check the URL is publicly reachable.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14, fontWeight: 550 }}>{a.summary ?? "Scored."}</span>
                <span className="num" style={{ fontSize: 12, color: "var(--text-faint)" }}>
                  {a.overall_score} / 100 · {shortTime(a.analyzed_at)}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", marginTop: 10 }}>
                {a.scores.map((s) => <CharRow key={s.id} score={s} />)}
              </div>
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--text-3)" }}>
                  Model and method
                </summary>
                <div style={{ fontSize: 12.5, color: "var(--text-4)", lineHeight: 1.55, marginTop: 6 }}>
                  {a.model}. Overall is the weighted mean of the ten characteristics (weights sum to 100).
                  Conversion numbers on the row are warehouse facts, not model output.
                </div>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CharRow({ score }: { score: LandingCharacteristicScore }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border-hairline)",
                  alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span style={{ fontSize: 13 }}>{score.label}</span>
          <span className="eyebrow">{score.weight}</span>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 2, lineHeight: 1.45 }}>
          {score.rationale}
        </div>
        <div style={{ height: 4, background: "var(--surface-inset)", borderRadius: 2, marginTop: 7 }}>
          <div style={{ height: "100%", width: `${score.score}%`, borderRadius: 2, background: scoreColor(score.score) }} />
        </div>
      </div>
      <span className="num" style={{ fontSize: 16, color: scoreColor(score.score), minWidth: 28, textAlign: "right" }}>
        {score.score}
      </span>
    </div>
  );
}

function Stat({
  label, value, deltaLabel, tone,
}: {
  label: string; value: string; deltaLabel?: string; tone?: "pos" | "neg" | "flat";
}) {
  return (
    <div style={{ minWidth: 72 }}>
      <div className="eyebrow">{label}</div>
      <div className="num" style={{ fontSize: 16, marginTop: 3 }}>{value}</div>
      <div className="num" style={{ fontSize: 11, color: tone ? toneColor(tone) : "var(--text-faint)", marginTop: 2 }}>
        {deltaLabel ?? "\u00a0"}
      </div>
    </div>
  );
}

function MiniDial({ score }: { score: number | null }) {
  const R = 18;
  const C = 2 * Math.PI * R;
  const frac = score === null ? 0 : score / 100;
  const color = scoreColor(score);
  return (
    <div style={{ position: "relative", width: 52, height: 52, flex: "none" }}
         title={score === null ? "Not analysed" : `Quality ${score}`}>
      <svg viewBox="0 0 52 52" style={{ width: 52, height: 52, transform: "rotate(-90deg)" }} aria-hidden>
        <circle cx="26" cy="26" r={R} fill="none" stroke="var(--surface-inset)" strokeWidth="5" />
        {score !== null && (
          <circle cx="26" cy="26" r={R} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
                  strokeDasharray={`${C * frac} ${C}`} />
        )}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="num" style={{ fontSize: 12, color: score === null ? "var(--text-faint)" : "var(--text)" }}>
          {score === null ? DASH : score}
        </span>
      </div>
    </div>
  );
}

function pct(rate: number | null): string {
  if (rate === null) return DASH;
  return `${(rate * 100).toFixed(1)}%`;
}

function scoreColor(score: number | null): string {
  if (score === null) return "var(--muted)";
  return score >= 70 ? "var(--pos)" : score >= 50 ? "var(--warn)" : "var(--neg)";
}

function PageLoading() {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--text-faint)" }}>Loading…</div>;
}
function PageProblem({ message }: { message: string }) {
  return <div className="page" style={{ padding: "60px 40px", color: "var(--neg)" }}>{message}</div>;
}
